import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Grid, Popconfirm, Switch, Table, Tag } from '@arco-design/web-react';
import type { ColumnProps } from '@arco-design/web-react/es/Table';

import { EmptyTips } from '@/components/EmptyTips';
import { PermissionSyncWrapper } from '@/components/PermissionSync';
import type { PermissionSyncWrapperProps } from '@/components/PermissionSync';
import {
  type InstanceNetworkServiceItem,
  canToggleInstanceExternalAccess,
} from '@/pages/data-center/instance-management/utils/externalAccess';
import { isMaasServiceExternalAccessEnabled } from '@/pages/data-center/instance-management/utils/externalAccessCore';

const Row = Grid.Row;
const Col = Grid.Col;

/** 服务项接口 */
interface ServiceItem extends InstanceNetworkServiceItem {
  /** 服务名称 */
  name: string;
  /** 协议类型 TCP/UDP/SCTP */
  protocol: string;
  /** 内部端口 */
  internalPort: number;
  /** 外部端口 */
  externalPort: number;
  /** 服务描述 */
  description: string;
  /** 是否为默认服务 */
  _isDefault?: boolean;
}

interface ServiceTableProps {
  /** 服务数据数组 - 用于展示实例的服务信息 */
  data?: ServiceItem[];
  /** 额外内容 */
  extra?: React.ReactNode;
  /** 是否展示外部访问 Switch 列 */
  showExternalAccessColumn?: boolean;
  /** 切换外部访问 */
  onExternalAccessToggle?: (service: ServiceItem, enable: boolean) => void | Promise<void>;
  /** 外部访问 Switch 切换中的服务名 */
  externalAccessTogglingServiceName?: string;
  /** 实例 phase，用于控制 Switch 是否可操作 */
  instancePhase?: string;
  /** 是否展示删除操作列 */
  showDeleteAction?: boolean;
  /** 删除单个服务 */
  onDeleteService?: (serviceName: string) => void | Promise<void>;
  /** 删除权限 */
  deletePermission?: PermissionSyncWrapperProps['permission'];
  /** 删除权限 authMap */
  deleteAuthMap?: PermissionSyncWrapperProps['authMap'];
}

export function ServiceTable({
  data,
  extra,
  showExternalAccessColumn,
  onExternalAccessToggle,
  externalAccessTogglingServiceName,
  instancePhase,
  showDeleteAction,
  onDeleteService,
  deletePermission,
  deleteAuthMap,
}: ServiceTableProps) {
  const { t } = useTranslation();
  const msg = {
    SERVICE_NAME: t('components:service.name'),
    PROTOCOL: t('components:protocol'),
    INTERNAL_PORT: t('components:internal.port'),
    EXTERNAL_PORT: t('components:external.port'),
    DESCRIPTION: t('common:description'),
    DEFAULT_SERVICE: t('components:default.service'),
    AUTO_ASSIGN: t('common:auto.assign'),
  };

  const enableExternalAccessToggle = canToggleInstanceExternalAccess(instancePhase);
  const canDeleteAny = !!(data && data.length > 1);

  const columns = useMemo(() => {
    const baseColumns: ColumnProps<ServiceItem>[] = [
      {
        key: 'name',
        title: msg.SERVICE_NAME,
        dataIndex: 'name',
        bodyCellStyle: { minWidth: 200 },
        render: (name: string, record: ServiceItem) => (
          <span>
            {name}
            {record._isDefault && (
              <Tag color="orange" size="small" style={{ marginLeft: 8 }}>
                {msg.DEFAULT_SERVICE}
              </Tag>
            )}
          </span>
        ),
      },
      {
        key: 'protocol',
        title: msg.PROTOCOL,
        dataIndex: 'protocol',
        bodyCellStyle: { minWidth: 120 },
      },
      {
        key: 'internalPort',
        title: msg.INTERNAL_PORT,
        dataIndex: 'internalPort',
        bodyCellStyle: { minWidth: 140 },
      },
      {
        key: 'externalPort',
        title: msg.EXTERNAL_PORT,
        dataIndex: 'externalPort',
        bodyCellStyle: { minWidth: 140 },
        render: (port: number) => {
          if (port === 0 || port === null || port === undefined) {
            return <span style={{ color: '#999' }}>{msg.AUTO_ASSIGN}</span>;
          }
          return port;
        },
      },
      {
        key: 'description',
        title: msg.DESCRIPTION,
        dataIndex: 'description',
        bodyCellStyle: { minWidth: 300 },
        render: (value) => value || '-',
      },
    ];

    if (showExternalAccessColumn) {
      baseColumns.push({
        key: 'externalAccess',
        title: t('common:external.access'),
        width: 120,
        render: (_, record) => (
          <Switch
            checked={isMaasServiceExternalAccessEnabled(record as unknown as Record<string, unknown>)}
            loading={externalAccessTogglingServiceName === record.name}
            disabled={!enableExternalAccessToggle || !record.name}
            onChange={(checked) => onExternalAccessToggle?.(record, checked)}
          />
        ),
      });
    }

    if (showDeleteAction) {
      baseColumns.push({
        key: 'operation',
        title: t('common:operation'),
        width: 100,
        render: (_, record) => {
          const canDelete = canDeleteAny && !!record.name;
          if (!canDelete || !deletePermission || !onDeleteService) {
            return '-';
          }
          return (
            <PermissionSyncWrapper permission={deletePermission} authMap={deleteAuthMap}>
              <Popconfirm
                focusLock
                title={t('auth-conf:delete.delete.service')}
                content={`${t('common:name')}: ${record.name}`}
                onOk={() => onDeleteService(record.name!)}
              >
                <Button type="text" status="danger" className="table-operation-btn">
                  {t('common:delete')}
                </Button>
              </Popconfirm>
            </PermissionSyncWrapper>
          );
        },
      });
    }

    return baseColumns;
  }, [
    canDeleteAny,
    deleteAuthMap,
    deletePermission,
    enableExternalAccessToggle,
    externalAccessTogglingServiceName,
    msg,
    onDeleteService,
    onExternalAccessToggle,
    showDeleteAction,
    showExternalAccessColumn,
    t,
  ]);

  const cellStyle = { background: '#fff', padding: '16px' };
  const toolbarStyle = {
    marginBottom: 16,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
  };

  return (
    <Row className="grid-demo" style={{ marginBottom: 16 }}>
      <Col span={24} style={cellStyle}>
        {extra ? <div style={toolbarStyle}>{extra}</div> : null}
        <Table columns={columns} data={data} pagination={false} noDataElement={<EmptyTips />} rowKey="name" />
      </Col>
    </Row>
  );
}
